import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import type { Podcast } from '../../../types/podcast'
import type { VoiceOption } from '../context/podcast-editor-provider'


export type ScriptJobStatus = 'idle' | 'running' | 'failed' | 'completed'

// Unified loading activity for concise status messages
export type LoadingActivity = {
  phase: 'script' | 'synthesis' | 'assemble' | 'script-refine'
  message: string
  percent?: number
  step: 'starting' | 'in_progress' | 'line_completed' | 'finalizing' | 'completed' | 'diff_ready'
  meta?: {
    total?: number
    completed?: number
    index?: number
    lineId?: string
    host?: string
  }
}

export type EditorState = {
  id: string
  podcast: Podcast | null
  voices: VoiceOption[]
  // UI state
  editingTitle: boolean
  titleDraft: string
  hostAVoiceId: string
  hostBVoiceId: string
  // New: audio settings
  speed: number
  volume: number
  emotion: string
  // New: language ISO code
  languageIso: string
  // script job lifecycle state
  scriptJobStatus: ScriptJobStatus
  scriptErrorMessage?: string | null
  // unified status banner
  activity: LoadingActivity | null
}

export type EditorActions = {
  // low-level mutators for commands only
  setPodcast: (podcast: Podcast) => void
  setVoices: (voices: VoiceOption[]) => void
  setHostAVoiceId: (id: string) => void
  setHostBVoiceId: (id: string) => void
  setSpeed: (v: number) => void
  setVolume: (v: number) => void
  setEmotion: (v: string) => void
  setLanguageIso: (iso: string) => void
  setEditingTitle: (v: boolean) => void
  setTitleDraft: (v: string) => void
  setScriptJobStatus: (status: ScriptJobStatus, errorMessage?: string | null) => void
  clearScriptError: () => void
  // Unified activity helpers
  setActivity: (activity: LoadingActivity | null) => void
  clearActivity: () => void
}

export const useEditorStore = create<EditorState & EditorActions>()(
  immer((set) => ({
    id: '',
    podcast: null,
    voices: [],
    editingTitle: false,
    titleDraft: '',
    hostAVoiceId: '',
    hostBVoiceId: '',
    speed: 1.0,
    volume: 1.0,
    emotion: 'neutral',
    languageIso: 'en',
    scriptJobStatus: 'idle',
    scriptErrorMessage: null,
    activity: null,

    setPodcast: (podcast) => set((s) => {
      s.podcast = podcast
      s.id = podcast.id
      s.hostAVoiceId = podcast.hostAVoiceId || s.hostAVoiceId
      s.hostBVoiceId = podcast.hostBVoiceId || s.hostBVoiceId
      s.speed = podcast.speed ?? s.speed
      s.volume = podcast.volume ?? s.volume
      s.emotion = podcast.emotion || s.emotion
      s.languageIso = podcast.languageIso || s.languageIso
      s.titleDraft = podcast.title
    }),
    setVoices: (voices) => set((s) => { s.voices = voices }),
    setHostAVoiceId: (id) => set((s) => { s.hostAVoiceId = id }),
    setHostBVoiceId: (id) => set((s) => { s.hostBVoiceId = id }),
    setSpeed: (v) => set((s) => { s.speed = v }),
    setVolume: (v) => set((s) => { s.volume = v }),
    setEmotion: (v) => set((s) => { s.emotion = v }),
    setLanguageIso: (iso) => set((s) => { s.languageIso = iso }),
    setEditingTitle: (v) => set((s) => { s.editingTitle = v }),
    setTitleDraft: (v) => set((s) => { s.titleDraft = v }),

    setScriptJobStatus: (status, errorMessage) => set((s) => {
      s.scriptJobStatus = status
      if (status === 'failed') s.scriptErrorMessage = errorMessage ?? s.scriptErrorMessage ?? 'Script generation failed.'
      else s.scriptErrorMessage = null
    }),
    clearScriptError: () => set((s) => { s.scriptErrorMessage = null }),

    // Unified activity setters
    setActivity: (activity) => set((s) => { s.activity = activity }),
    clearActivity: () => set((s) => { s.activity = null }),
  }))
)

// selectors
export const selectors = {
  podcast: (s: EditorState) => s.podcast,
  voices: (s: EditorState) => s.voices,
  hostA: (s: EditorState) => s.hostAVoiceId,
  hostB: (s: EditorState) => s.hostBVoiceId,
  audioSettings: (s: EditorState) => ({ speed: s.speed, volume: s.volume, emotion: s.emotion }),
  languageIso: (s: EditorState) => s.languageIso,
  editingTitle: (s: EditorState) => s.editingTitle,
  titleDraft: (s: EditorState) => s.titleDraft,
}
